import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import { FS } from "src/configs/constance";
import HeuristicFarmingSDK from "src/contract-logic/logic/HeuristicFarmingSDK";
import { getCurrentContractAddresses } from "src/configs/address";
import { getCurrentNetwork } from "src/configs/connection-config";
import { ethers } from "ethers";

export const fetchAllVaultsData = createAsyncThunk("heuristicFarmingSlice/fetchAllVaultsData", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  if (!accountAddress) {
    throw new Error("Wallet not connected");
  }

  const network = getCurrentNetwork();
  const addresses = getCurrentContractAddresses();
  const provider = new ethers.providers.JsonRpcProvider(network.rpcUrls[0]);

  const CHAIN_ID = network.chainId === "0x38" ? 56 : 97;

  const vaultsData = await HeuristicFarmingSDK.getHeuristicFarmingData(
    accountAddress,
    provider,
    CHAIN_ID,
    addresses.NFT_FARMING_BASE_EXP,
    addresses.NFT_COLLECTION_ADDRESS,
    addresses.NFT_CORE_ADDRESS
  );

  for (const [vaultId, vaultData] of vaultsData) {
    thunkAPI.dispatch(heuristicFarmingSlice.actions.setVaultData({ vaultId, data: vaultData }));
  }
});

export const fetchVaultData = createAsyncThunk(
  "heuristicFarmingSlice/fetchVaultData",
  async ({ vaultId }, thunkAPI) => {
    const accountAddress = thunkAPI.getState().accountSlice.address;

    if (!accountAddress) {
      throw new Error("Wallet not connected");
    }

    const network = getCurrentNetwork();
    const addresses = getCurrentContractAddresses();
    const provider = new ethers.providers.JsonRpcProvider(network.rpcUrls[0]);

    const CHAIN_ID = network.chainId === "0x38" ? 56 : 97;

    const vaultsData = await HeuristicFarmingSDK.getHeuristicFarmingData(
      accountAddress,
      provider,
      CHAIN_ID,
      addresses.NFT_FARMING_BASE_EXP,
      addresses.NFT_COLLECTION_ADDRESS,
      addresses.NFT_CORE_ADDRESS
    );

    const vaultData = vaultsData.get(vaultId);
    if (!vaultData) {
      throw new Error(`Vault ${vaultId} not found`);
    }

    return vaultData;
  }
);

const initialState = {
  vaults: {},
  fetchingStatus: FS.INITIAL,
};

const heuristicFarmingSlice = createSlice({
  name: "heuristicFarmingSlice",
  initialState: initialState,
  reducers: {
    setVaultData: (state, action) => {
      const { vaultId, data } = action.payload;
      state.vaults[vaultId] = data;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchAllVaultsData.pending, (state, _) => {
        state.fetchingStatus = FS.FETCHING;
      })
      .addCase(fetchAllVaultsData.rejected, (state, action) => {
        state.fetchingStatus = FS.FAIL;
        console.error(action);
      })
      .addCase(fetchAllVaultsData.fulfilled, (state) => {
        state.fetchingStatus = FS.SUCCESS;
      })
      .addCase(fetchVaultData.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], ...action.payload };
      })
      .addCase(fetchVaultData.rejected, (state, action) => {
        console.error(action);
      }),
});

export default heuristicFarmingSlice.reducer;
export const { setVaultData } = heuristicFarmingSlice.actions;

export const selectHeuristicFarmingSlice = (state) => state.heuristicFarmingSlice;

export const selectAllVaults = createDraftSafeSelector(
  (state) => selectHeuristicFarmingSlice(state).vaults,
  (vaults) => {
    if (!vaults) return [];
    const res = Object.entries(vaults).map(([_, item]) => item);
    res.sort((v1, v2) => v1.level - v2.level);
    return res;
  }
);
